import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

type PeriodKey = 'week' | 'month' | 'year';

const PERIOD_LABELS: Record<PeriodKey, string> = { week: '7d', month: '30d', year: '1y' };

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  link?: string;
  percentage?: number;
  periodSelector?: {
    active: PeriodKey;
    onChange: (period: PeriodKey) => void;
  };
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, link, percentage, periodSelector }) => {
  const navigate = useNavigate();
  const isPositive = percentage !== undefined && percentage >= 0;

  return (
    <Box
      onClick={() => link && navigate(link)}
      sx={{
        p: { xs: 0.75, sm: 0.875, md: 1, lg: 1.125 },
        borderRadius: 2,
        border: (theme: any) => `1px solid ${theme.palette.border.default}`,
        cursor: link ? 'pointer' : 'default',
        backgroundColor: 'transparent',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        width: '100%',
        position: 'relative',
      }}
    >
      {periodSelector && (
        <Stack
          direction="column"
          spacing={0}
          onClick={(e) => e.stopPropagation()}
          sx={{ position: 'absolute', top: 0, bottom: 0, right: 4, justifyContent: 'space-evenly' }}
        >
          {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((key) => (
            <Box
              key={key}
              onClick={() => periodSelector.onChange(key)}
              sx={{
                px: 0.5,
                py: 0.125,
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.6rem',
                fontWeight: periodSelector.active === key ? 700 : 400,
                color: periodSelector.active === key ? 'text.purple' : 'text.grey',
                backgroundColor: periodSelector.active === key ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.06)',
                },
                transition: 'all 0.15s ease',
                lineHeight: 1.4,
                textAlign: 'center',
              }}
            >
              {PERIOD_LABELS[key]}
            </Box>
          ))}
        </Stack>
      )}
      <Stack spacing={{ xs: 0.4, sm: 0.5, md: 0.75, lg: 0.875 }}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 0.4, sm: 0.5, md: 0.75 }}>
          <Box sx={{
            color: 'text.grey',
            display: 'flex',
            alignItems: 'center',
            '& svg': {
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              position: 'relative',
              top: '-1px',
            }
          }}>
            {icon}
          </Box>
          <Typography
            sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              color: 'text.grey',
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="baseline" spacing={{ xs: 0.4, sm: 0.5, md: 0.75 }}>
          <Typography
            sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.heading3,
              fontWeight: 800,
              color: (theme: any) => theme.palette.mode === 'dark' ? '#ffffff' : 'text.purple',
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {percentage !== undefined && (
            <Stack direction="row" alignItems="center" spacing={{ xs: 0.2, sm: 0.4 }}>
              {isPositive ? (
                <TrendingUpIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small, color: 'text.successGreen', opacity: 0.8 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small, color: 'text.red', opacity: 0.8 }} />
              )}
              <Typography
                sx={{
                  fontSize: (theme: any) => theme.typography.fontSizes.small,
                  fontWeight: 500,
                  color: isPositive ? 'text.successGreen' : 'text.red',
                  opacity: 0.8,
                }}
              >
                {Math.abs(percentage).toFixed(1)}%
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default MetricCard;
